import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Emoji, EmojiStyle } from 'emoji-picker-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { countries, getCitiesByCountry, heightsCm } from '../../data/selectOptions';
import { FiUser, FiBriefcase, FiHeart, FiSettings, FiMoreVertical, FiEdit2, FiTrash2, FiX, FiCamera, FiImage } from 'react-icons/fi';

import ImageCrop from '../../components/ImageCrop';
import Modal from '../../components/Modal';

import { useTheme } from '../../context/ThemeContext';
import { buildApiUrl, buildMediaUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';

const Profile = () => {
  // Render message text with Facebook-style emoji glyphs
  const emojiRegex = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)/gu;
  const isEmojiSegment = (segment) => {
    if (!segment) return false;
    emojiRegex.lastIndex = 0;
    return emojiRegex.test(segment);
  };
  const toUnified = (segment) => segment
    .split('\u200d')
    .map(part => [...part].map(ch => ch.codePointAt(0).toString(16)).join('-'))
    .join('-200d-');
  const renderEmojiText = (text) => {
    if (!text) return null;
    const parts = text.split(emojiRegex).filter(Boolean);
    return parts.map((part, idx) =>
      isEmojiSegment(part) ? (
        <span key={`emoji-${idx}`} className="inline-block px-px align-bottom">
          <Emoji
            unified={toUnified(part)}
            size={22}
            emojiStyle={EmojiStyle.FACEBOOK}
          />
        </span>
      ) : (
        <span key={`text-${idx}`}>{part}</span>
      )
    );
  };
  const { user, updateUser } = useAuth();
  const { success, error } = useToast();
  const { theme } = useTheme();
  const PROFILE_PHOTO_MAX_MB = 15;
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);


  const fileInputRef = useRef(null);

  const [myPosts, setMyPosts] = useState([]);
  const [myPostsLoading, setMyPostsLoading] = useState(false);
  const [myPostsError, setMyPostsError] = useState('');
  const [myPostDropdown, setMyPostDropdown] = useState(null);
  const [editingMyPost, setEditingMyPost] = useState(null);
  const [editingMyPostContent, setEditingMyPostContent] = useState('');
  const [isUpdatingMyPost, setIsUpdatingMyPost] = useState(false);
  const [deletingMyPostId, setDeletingMyPostId] = useState(null);
  const [myPostsCursor, setMyPostsCursor] = useState(null);
  const [myPostsHasMore, setMyPostsHasMore] = useState(false);
  const [myPostsLoadingMore, setMyPostsLoadingMore] = useState(false);
  const myPostsSentinelRef = useRef(null);

  // Initialize profileData with user data and update when user changes
  const [profileData, setProfileData] = useState({
    // Personal Info
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',


    gender: user?.gender || '',
    aboutMe: user?.aboutMe || '',
    dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
    maritalStatus: user?.maritalStatus || '',
    height: user?.height || '',
    country: user?.country || '',
    city: user?.city || '',

    // Professional Info
    education: user?.education || '',
    profession: user?.profession || '',

    company: user?.company || '',
    workExperience: user?.workExperience || '',

    // Religious Info
    religion: user?.religion || '',
    community: user?.community || '',
    caste: user?.caste || '',
    subCaste: user?.subCaste || '',



    // Preferences
    preferredAgeFrom: user?.preferences?.preferredAgeFrom || 25,
    preferredAgeTo: user?.preferences?.preferredAgeTo || 35,
    preferredReligion: user?.preferences?.preferredReligion || '',
    preferredEducation: user?.preferences?.preferredEducation || '',
    preferredCountry: user?.preferences?.preferredCountry || '',
    preferredCity: user?.preferences?.preferredCity || '',
    preferredIncome: user?.preferences?.preferredIncome || '',
    preferredProfession: user?.preferences?.preferredProfession || ''
  });

  // Calculate profile completion percentage based on current profileData
  const calculateProfileCompletion = () => {
    const source = profileData || user || {};
    const requiredFields = [
      'firstName', 'lastName', 'gender',
      'dateOfBirth', 'country', 'city', 'maritalStatus',
      'education', 'profession'
    ];

    const optionalFields = [
      'religion', 'community', 'profilePhoto', 'height',
      'company', 'workExperience'
    ];

    const isFilled = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'number') return !Number.isNaN(value) && value !== 0;
      return true;
    };

    let completed = 0;
    const total = requiredFields.length + optionalFields.length;

    requiredFields.forEach(field => {
      if (isFilled(source[field])) completed++;
    });

    optionalFields.forEach(field => {
      if (isFilled(source[field])) completed++;
    });

    const percentage = Math.round((completed / total) * 100);
    return Math.min(percentage, 100);
  };

  const profileCompletion = calculateProfileCompletion();

  // Update profileData when user data changes (e.g., after save)
  useEffect(() => {
    if (user) {
      // Helper to match country code or name
      const getCountryCode = (input) => {
        if (!input) return '';
        // Check if input matches a country value (code)
        if (countries.some(c => c.value === input)) return input;
        // Check if input matches a country label (name)
        const found = countries.find(c => c.label.toLowerCase() === input.toLowerCase());
        return found ? found.value : input;
      };

      setProfileData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',


        gender: user.gender || '',
        aboutMe: user.aboutMe || '',
        dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
        maritalStatus: user.maritalStatus || '',
        height: user.height || '',
        country: getCountryCode(user.country),
        city: user.city || '',
        education: user.education || '',
        profession: user.profession || '',

        company: user.company || '',
        workExperience: user.workExperience || '',
        religion: user.religion || '',
        community: user.community || '',
        caste: user.caste || '',
        subCaste: user.subCaste || '',

        preferredAgeFrom: user.preferences?.preferredAgeFrom || 25,
        preferredAgeTo: user.preferences?.preferredAgeTo || 35,
        preferredReligion: user.preferences?.preferredReligion || '',
        preferredEducation: user.preferences?.preferredEducation || '',
        preferredCountry: getCountryCode(user.preferences?.preferredCountry),
        preferredCity: user.preferences?.preferredCity || '',
        preferredIncome: user.preferences?.preferredIncome || '',
        preferredProfession: user.preferences?.preferredProfession || ''
      }));
    }
  }, [user]);

  const dispatchPostsUpdated = () => {
    window.dispatchEvent(new Event('posts_updated'));
  };

  const MY_POSTS_PAGE_SIZE = 15;

  const mapMyPostsResponse = useCallback((items = []) => {
    if (!Array.isArray(items)) return [];
    return items
      .filter(post => !post?.author || (String(post?.author?._id || post?.author?.id) === String(user?.id)))
      .map(post => ({
        id: post._id,
        content: post.content,
        image: post.image ? buildMediaUrl(post.image) : null,
        timestamp: post.timestampFormatted || '',
        likes: post.likesCount ?? (Array.isArray(post.likes) ? post.likes.length : 0)
      }));
  }, [user?.id]);

  const fetchMyPosts = useCallback(async () => {
    if (!user?.id) return;

    setMyPostsLoading(true);
    setMyPostsError('');
    setMyPostsCursor(null);
    setMyPostsHasMore(false);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('author', user.id);
      params.set('limit', String(MY_POSTS_PAGE_SIZE));
      const { response, data } = await fetchJson(buildApiUrl(`/api/posts?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && Array.isArray(data?.data)) {
        const mapped = mapMyPostsResponse(data.data);
        setMyPosts(mapped);
        setMyPostsCursor(data?.pagination?.nextCursor ?? null);
        setMyPostsHasMore(Boolean(data?.pagination?.hasMore));
      } else {
        setMyPosts([]);
        setMyPostsError(data?.message || 'Failed to load your posts');
      }
    } catch (err) {
      console.error('Error fetching my posts:', err);
      setMyPosts([]);
      setMyPostsError(formatFetchError(err, 'Failed to load your posts'));
    } finally {
      setMyPostsLoading(false);
    }
  }, [MY_POSTS_PAGE_SIZE, mapMyPostsResponse, user?.id]);

  const loadMoreMyPosts = useCallback(async () => {
    if (!user?.id) return;
    if (!myPostsHasMore || myPostsLoadingMore || !myPostsCursor) return;

    try {
      setMyPostsLoadingMore(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('author', user.id);
      params.set('limit', String(MY_POSTS_PAGE_SIZE));
      params.set('cursor', String(myPostsCursor));

      const { response, data } = await fetchJson(buildApiUrl(`/api/posts?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && Array.isArray(data?.data)) {
        const mapped = mapMyPostsResponse(data.data);
        setMyPosts((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const deduped = mapped.filter((p) => !seen.has(p.id));
          return [...prev, ...deduped];
        });
        setMyPostsCursor(data?.pagination?.nextCursor ?? null);
        setMyPostsHasMore(Boolean(data?.pagination?.hasMore));
      } else {
        setMyPostsHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more my posts:', err);
      setMyPostsHasMore(false);
    } finally {
      setMyPostsLoadingMore(false);
    }
  }, [MY_POSTS_PAGE_SIZE, mapMyPostsResponse, myPostsCursor, myPostsHasMore, myPostsLoadingMore, user?.id]);

  useEffect(() => {
    fetchMyPosts();
  }, [fetchMyPosts]);

  useEffect(() => {
    const handlePostsUpdated = () => {
      setMyPostsCursor(null);
      setMyPostsHasMore(false);
      fetchMyPosts();
    };

    window.addEventListener('posts_updated', handlePostsUpdated);
    return () => {
      window.removeEventListener('posts_updated', handlePostsUpdated);
    };
  }, [fetchMyPosts]);

  useEffect(() => {
    const sentinel = myPostsSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreMyPosts();
        }
      },
      { root: null, rootMargin: '250px 0px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreMyPosts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('[data-profile-post-dropdown]');
      let clickedOutside = true;

      dropdowns.forEach(dropdown => {
        if (dropdown.contains(event.target)) {
          clickedOutside = false;
        }
      });

      if (clickedOutside) {
        setMyPostDropdown(null);
      }
    };

    if (myPostDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [myPostDropdown]);

  const handleMyPostDropdownClick = (e, postId) => {
    e.stopPropagation();
    setMyPostDropdown(prev => (prev === postId ? null : postId));
  };

  const handleEditMyPost = (post) => {
    setEditingMyPost(post);
    setEditingMyPostContent(post?.content || '');
    setMyPostDropdown(null);
  };

  const handleUpdateMyPost = async () => {
    const content = editingMyPostContent.trim();
    if (!editingMyPost?.id) return;
    if (!content) {
      error('Post content cannot be empty');
      return;
    }

    try {
      setIsUpdatingMyPost(true);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/posts/${editingMyPost.id}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (response.ok && data?.data) {
        success('Post updated successfully!');
        setEditingMyPost(null);
        setEditingMyPostContent('');
        dispatchPostsUpdated();
        fetchMyPosts();
      } else {
        error(data?.message || 'Failed to update post');
      }
    } catch (err) {
      console.error('Error updating post:', err);
      error(formatFetchError(err, 'Failed to update post'));
    } finally {
      setIsUpdatingMyPost(false);
    }
  };

  const handleDeleteMyPost = async (postId) => {
    if (!postId) return;
    try {
      setDeletingMyPostId(postId);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/posts/${postId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && (data?.success ?? true)) {
        success('Post deleted successfully!');
        setMyPostDropdown(null);
        setMyPosts(prev => prev.filter(p => p.id !== postId));
        dispatchPostsUpdated();
        fetchMyPosts();
      } else {
        error(data?.message || 'Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      error(formatFetchError(err, 'Failed to delete post'));
    } finally {
      setDeletingMyPostId(null);
    }
  };

  const handleRemovePhoto = () => {
    setShowPhotoOptions(false);
    setShowDeleteConfirm(true);
  };

  const confirmRemovePhoto = async () => {
    try {
      setIsUploading(true);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/users/profile-photo'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && data.success) {
        updateUser({ ...user, profilePhoto: null });
        success('Profile photo removed successfully');
        setShowDeleteConfirm(false);
      } else {
        error(data?.message || 'Failed to remove photo');
      }
    } catch (err) {
      console.error('Error removing photo:', err);
      error('Failed to remove photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {

    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size
    if (file.size > PROFILE_PHOTO_MAX_MB * 1024 * 1024) {
      error(`File size should be less than ${PROFILE_PHOTO_MAX_MB}MB`);
      return;
    }

    // Create image URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCropModal(true);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedFile) => {
    const imageToRevoke = selectedImage;
    try {
      setShowCropModal(false);
      setSelectedImage(null);
      setIsUploading(true);

      const formData = new FormData();
      formData.append('profilePhoto', croppedFile);

      const token = localStorage.getItem('token');

      const { response, data } = await fetchJson(buildApiUrl('/api/users/upload-profile-photo'), {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && data.success) {
        // Update user context with new profile photo
        const updatedUser = { ...user, profilePhoto: data.profilePhoto };
        updateUser(updatedUser);
        success('Profile picture updated successfully!');
      } else {
        error(data?.message || 'Failed to upload profile picture');
      }
    } catch (uploadError) {
      console.error('Error uploading profile photo:', uploadError);
      if (uploadError?.status === 413) {
        error(`File size should be less than ${PROFILE_PHOTO_MAX_MB}MB`);
      } else {
        error(formatFetchError(uploadError, 'Error uploading profile picture. Please try again.'));
      }
    } finally {
      setIsUploading(false);
      // Clean up the image URL
      if (imageToRevoke) {
        URL.revokeObjectURL(imageToRevoke);
      }
    }
  };

  const handleCropCancel = () => {
    // Clean up the image URL
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }
    setShowCropModal(false);
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleSave = async () => {
    try {
      console.log('Saving profile:', profileData);
      setIsSaving(true);

      const token = localStorage.getItem('token');

      const { response, data } = await fetchJson(buildApiUrl('/api/users/update-profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok && data.success) {
        updateUser(data.user);
        setIsEditing(false);
        success('Profile updated successfully!');
      } else {
        error('Failed to update profile: ' + (data?.message || 'Unknown error'));
      }
    } catch (updateError) {
      console.error('Error updating profile:', updateError);
      error(formatFetchError(updateError, 'Error updating profile. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Reset city fields when country changes
    if (name === 'country') {
      setProfileData({
        ...profileData,
        [name]: value,
        city: '' // Reset personal city when country changes
      });

    } else if (name === 'preferredCountry') {
      setProfileData({
        ...profileData,
        [name]: value,
        preferredCity: '' // Reset preferred city when preferred country changes
      });
    } else {
      setProfileData({
        ...profileData,
        [name]: value
      });
    }
  };

  const renderFormField = (label, name, type = 'text', options = [], rows = 4) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      {type === 'select' ? (
        <select
          name={name}
          value={profileData[name]}
          onChange={handleChange}
          disabled={!isEditing}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 disabled:text-gray-900"
        >
          <option value="">Select {label}</option>
          {options.map((option, index) => (
            <option key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          name={name}
          value={profileData[name]}
          onChange={handleChange}
          disabled={!isEditing}
          rows={rows}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 disabled:text-gray-900"
        />
      ) : (
        <input
          type={type}
          name={name}
          value={profileData[name]}
          onChange={handleChange}
          disabled={!isEditing}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 disabled:text-gray-900"
        />
      )}
    </div>
  );

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl shadow-soft p-8 mb-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-32 h-32 bg-gradient-to-br from-romantic-500 to-primary-500 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-lg overflow-hidden">
                {user?.profilePhoto ? (
                  <img
                    src={buildMediaUrl(user.profilePhoto)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>{user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}</span>
                )}
              </div>
              {isEditing && (
                <>

                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                    disabled={isUploading}
                  />
                  <div className="absolute -bottom-2 -right-2">
                    <button
                      onClick={() => setShowPhotoOptions(!showPhotoOptions)}
                      disabled={isUploading}
                      className="bg-romantic-500 text-white p-3 rounded-full shadow-lg hover:bg-romantic-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? (
                        <svg className="animate-spin w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <FiCamera className="w-5 h-5" />
                      )}
                    </button>

                    {showPhotoOptions && (
                      <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-xl z-50 overflow-hidden border border-gray-100 flex flex-col transform origin-bottom-right">
                        <button
                          onClick={() => { setShowPhotoOptions(false); triggerFileInput(); }}
                          className="text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 text-gray-700 transition-colors border-b border-gray-50"
                        >
                          <FiImage size={18} /> <span className="font-medium">Choose Photo</span>
                        </button>

                        {user?.profilePhoto && (
                          <button
                            onClick={handleRemovePhoto}
                            className="text-left px-4 py-3 hover:bg-red-50 flex items-center space-x-3 text-red-600 transition-colors border-b border-gray-50"
                          >
                            <FiTrash2 size={18} /> <span className="font-medium">Remove Photo</span>
                          </button>
                        )}

                        <button
                          onClick={() => setShowPhotoOptions(false)}
                          className="text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 text-gray-500 transition-colors"
                        >
                          <FiX size={18} /> <span className="font-medium">Cancel</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {user?.firstName} {user?.lastName}
              </h1>
              <p className="text-gray-600 text-lg mb-4">{user?.username}</p>

              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                <div className="bg-gradient-to-r from-romantic-50 to-primary-50 px-4 py-2 rounded-full text-romantic-700 font-semibold border border-romantic-200">
                  {user?.membershipType === 'premium' ? '⭐ Premium Member' : 'Free Member'}
                </div>
                <div className="bg-gray-50 px-4 py-2 rounded-full text-gray-600 font-medium">
                  Profile: {profileCompletion}% Complete
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex space-x-4">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${isEditing
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gradient-to-r from-romantic-500 to-primary-500 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  }`}
              >
                {isEditing ? 'Cancel Editing' : 'Edit Profile'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-3xl shadow-soft p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Sections</h3>
              <nav className="space-y-2">
                {[
                  { id: 'personal', label: 'Personal Information', icon: <FiUser size={20} /> },
                  { id: 'professional', label: 'Professional Details', icon: <FiBriefcase size={20} /> },
                  { id: 'religious', label: 'Religious Background', icon: <FiHeart size={20} /> },

                  { id: 'preferences', label: 'Partner Preferences', icon: <FiSettings size={20} /> }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${activeTab === tab.id
                      ? 'bg-gradient-to-r from-romantic-50 to-primary-50 text-romantic-700 font-semibold border border-romantic-200'
                      : theme === 'dark'
                        ? 'text-white hover:bg-white hover:text-slate-900'
                        : 'text-gray-900 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                  >
                    <span className="text-romantic-600">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-3xl shadow-soft p-8">
              {/* Tab Content */}
              {activeTab === 'personal' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Personal Information</h2>
                  <div className="mb-6">
                    {renderFormField('About Me', 'aboutMe', 'textarea', [], 4)}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderFormField('First Name', 'firstName')}
                    {renderFormField('Last Name', 'lastName')}



                    {renderFormField('Marital Status', 'maritalStatus', 'select', [
                      { value: 'single', label: 'Single' },
                      { value: 'divorced', label: 'Divorced' },
                      { value: 'widowed', label: 'Widowed' },
                      { value: 'separated', label: 'Separated' }
                    ])}
                    {renderFormField('Height (cm)', 'height', 'select', heightsCm)}
                    {renderFormField('Country', 'country', 'select', countries)}
                    {renderFormField('City', 'city', 'select', getCitiesByCountry(profileData.country))}
                  </div>
                </div>
              )}

              {activeTab === 'professional' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Professional Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderFormField('Education', 'education', 'select', [
                      { value: 'high-school', label: 'High School' },
                      { value: 'diploma', label: 'Diploma' },
                      { value: 'bachelor', label: "Bachelor's Degree" },
                      { value: 'master', label: "Master's Degree" },
                      { value: 'phd', label: 'PhD' },
                      { value: 'post-doctoral', label: 'Post Doctoral' }
                    ])}
                    {renderFormField('Profession', 'profession')}
                    {renderFormField('Company/Organization', 'company')}
                    {renderFormField('Work Experience (years)', 'workExperience', 'number')}

                  </div>
                </div>
              )}

              {activeTab === 'religious' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Religious Background</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderFormField('Religion', 'religion', 'select', [
                      { value: 'Islam', label: 'Islam' },
                      { value: 'Christianity', label: 'Christianity' },
                      { value: 'Hinduism', label: 'Hinduism' },
                      { value: 'Buddhism', label: 'Buddhism' },
                      { value: 'other', label: 'Other' }
                    ])}
                    {renderFormField('Community', 'community')}
                    {renderFormField('Caste', 'caste')}
                    {renderFormField('Sub-caste', 'subCaste')}
                  </div>
                </div>
              )}



              {activeTab === 'preferences' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Partner Preferences</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderFormField('Preferred Age From', 'preferredAgeFrom', 'number')}
                    {renderFormField('Preferred Age To', 'preferredAgeTo', 'number')}
                    {renderFormField('Preferred Religion', 'preferredReligion', 'select', [
                      { value: 'Islam', label: 'Islam' },
                      { value: 'Christianity', label: 'Christianity' },
                      { value: 'Hinduism', label: 'Hinduism' },
                      { value: 'Buddhism', label: 'Buddhism' },
                      { value: 'other', label: 'Other' }
                    ])}
                    {renderFormField('Preferred Education', 'preferredEducation', 'select', [
                      { value: 'bachelor', label: "Bachelor's Degree" },
                      { value: 'master', label: "Master's Degree" },
                      { value: 'phd', label: 'PhD' },
                      { value: 'any', label: 'Any' }
                    ])}
                    {renderFormField('Preferred Country', 'preferredCountry', 'select', countries)}
                    {renderFormField('Preferred City', 'preferredCity', 'select',
                      getCitiesByCountry(profileData.preferredCountry)
                    )}
                    {renderFormField('Preferred Income', 'preferredIncome', 'select', [
                      { value: '0-5', label: '0-5 Lakhs' },
                      { value: '5-10', label: '5-10 Lakhs' },
                      { value: '10-20', label: '10-20 Lakhs' },
                      { value: '20+', label: '20+ Lakhs' },
                      { value: 'any', label: 'Any' }
                    ])}
                    {renderFormField('Preferred Profession', 'preferredProfession')}
                  </div>
                </div>
              )}

              {/* Save Button */}
              {isEditing && (
                <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                    disabled={isSaving}
                  >
                    Discard Changes
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`px-8 py-3 bg-gradient-to-r from-romantic-500 to-primary-500 text-white rounded-xl font-semibold shadow-lg transition-all duration-200 ${isSaving ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl transform hover:-translate-y-0.5'}`}
                  >
                    {isSaving ? (
                      <span className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        <span>Saving...</span>
                      </span>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-3xl shadow-soft p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">My Posts</h2>
            <div className="text-sm text-gray-600">
              {myPosts.length} {myPosts.length === 1 ? 'post' : 'posts'}
            </div>
          </div>

          {myPostsLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-romantic-500"></div>
              <span className="ml-3 text-gray-600">Loading your posts...</span>
            </div>
          ) : myPostsError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {myPostsError}
            </div>
          ) : myPosts.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex justify-center text-5xl mb-4 text-romantic-400"><FiHeart /></div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">No posts yet</h3>
              <p className="text-gray-600">Share something from the Posts page and it will appear here.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {myPosts.map((post) => (
                <div key={post.id} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-visible">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-romantic-500 to-primary-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                          {user?.profilePhoto ? (
                            <img
                              src={buildMediaUrl(user.profilePhoto)}
                              alt={`${user.firstName} ${user.lastName}`}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span>{user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{user?.firstName} {user?.lastName}</div>
                          <div className="text-sm text-gray-500">{post.timestamp}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-sm text-gray-600 inline-flex items-center">
                          <FiHeart size={16} className="mr-2 text-romantic-500" />
                          {post.likes}
                        </div>
                        <div className="relative z-20" data-profile-post-dropdown>
                          <button
                            type="button"
                            onClick={(e) => handleMyPostDropdownClick(e, post.id)}
                            className="p-2 rounded-lg hover:bg-white transition-colors"
                          >
                            <FiMoreVertical className="text-gray-500" size={18} />
                          </button>

                          {myPostDropdown === post.id && (
                            <div className="absolute right-0 top-10 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                              <button
                                type="button"
                                onClick={() => handleEditMyPost(post)}
                                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                              >
                                <FiEdit2 size={16} />
                                <span>Edit Post</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteMyPost(post.id)}
                                disabled={deletingMyPostId === post.id}
                                className={`w-full px-4 py-2 text-left transition-colors flex items-center space-x-2 ${deletingMyPostId === post.id
                                  ? 'text-red-300 cursor-not-allowed'
                                  : 'text-red-600 hover:bg-red-50'
                                  }`}
                              >
                                <FiTrash2 size={16} />
                                <span>{deletingMyPostId === post.id ? 'Deleting…' : 'Delete Post'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-gray-800 whitespace-pre-line break-words">{renderEmojiText(post.content)}</div>

                    {post.image && (
                      <div className="mt-5 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center h-96">
                        <img
                          src={post.image}
                          alt="Post"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div ref={myPostsSentinelRef} className="h-10" />

              {myPostsLoadingMore && (
                <div className="flex justify-center items-center py-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-romantic-500"></div>
                  <span className="ml-3 text-gray-600 text-sm">Loading more…</span>
                </div>
              )}

              {!myPostsLoadingMore && myPostsHasMore && (
                <div className="text-center text-xs text-gray-500">Scroll to load more</div>
              )}
            </div>
          )}
        </div>
      </div>

      {editingMyPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Edit Post</h2>
                <button
                  type="button"
                  onClick={() => {
                    setEditingMyPost(null);
                    setEditingMyPostContent('');
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FiX className="text-gray-500" size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <textarea
                value={editingMyPostContent}
                onChange={(e) => setEditingMyPostContent(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-romantic-500 focus:border-transparent resize-none"
                rows={4}
              />

              {editingMyPost.image && (
                <div className="mt-4 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center h-80">
                  <img
                    src={editingMyPost.image}
                    alt="Post"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditingMyPost(null);
                    setEditingMyPostContent('');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isUpdatingMyPost}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateMyPost}
                  disabled={isUpdatingMyPost}
                  className="px-6 py-2 bg-romantic-500 text-white rounded-lg hover:bg-romantic-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isUpdatingMyPost ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        size="sm"
      >
        <div className="p-6 text-center">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-6">
            <FiTrash2 className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Remove Profile Photo?</h3>
          <p className="text-gray-500 mb-8">
            Are you sure you want to remove your profile photo? This action cannot be undone.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemovePhoto}
              disabled={isUploading}
              className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isUploading ? 'Removing...' : 'Yes, Remove It'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Image Crop Modal */
      }
      {showCropModal && selectedImage && (
        <ImageCrop
          image={selectedImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          onClose={() => setShowCropModal(false)}
          aspectRatio={1}
          cropShape="round"
          showGrid={false}
        />
      )}
    </>
  );
};

export default Profile;
